import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { NOTIFICATION_ICON } from '../../../../app.component';
import { MatRadioModule } from '@angular/material/radio';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NotificationService } from '../../../../services/notification.service';
import { AiAgentResponse } from '../../../../models/responses/ai-agent-response';
import { NotificationResponse } from '../../../../models/responses/notification-response';
import { NotificationType } from '../../../../models/enums/notification-type';
import { CreateNotificationIntegrationRequest } from '../../../../models/requests/create-notification-integration-request';
import { UpdateNotificationIntegrationRequest } from '../../../../models/requests/update-notification-integration-request';
import { AiConfigurationPageEventsService } from '../../../../events/ai-configuration-page-events.service';
import { FormEntryResponse } from '../../../../models/responses/form-entry-response';
import { FormEntryService } from '../../../../services/form-entry.service';
import { FIELD_DESCRIPTION_VALIDATOR, FIELD_KEY_VALIDATOR, FIELD_NAME_VALIDATOR } from '../zapier-integration-dialog/zapier-integration-dialog.component';
import { FormEntriesComponent } from '../form-entries/form-entries.component';

const TARGET_SPLIT_CHAR = ',';
@Component({
  selector: 'app-notification-integration',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    NgFor,
    MatIconModule,
    MatDialogModule,
    AsyncPipe,
    MatSelectModule,
    ReactiveFormsModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    MatAutocompleteModule,
    MatCheckboxModule,
    MatRadioModule,
    MatIconModule,
    NgIf,
    FormEntriesComponent
  ],
  templateUrl: './notification-integration.component.html',
  styleUrl: './notification-integration.component.scss'
})
export class NotificationIntegrationComponent {
  notificationIcon: string = NOTIFICATION_ICON;
  isLoading: boolean = false;
  notificationTypes = Object.values(NotificationType)
  notificationForm!: FormGroup;


  constructor(
    private notificationIntegrationService: NotificationService,
    private aiConfigurationPageEventService: AiConfigurationPageEventsService,
    private formEntryService: FormEntryService,
    public dialogRef: MatDialogRef<NotificationIntegrationComponent>,
    private formBuilder: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: { aiAgent: AiAgentResponse, notification: NotificationResponse },
    private snackBar: MatSnackBar
  ) { }

  ngOnInit() {
    this.notificationForm = this.formBuilder.group({
      // target: [this.data.notification ? this.data.notification.target : "",Validators.required],
      type: [this.data.notification ? this.data.notification.type : "",Validators.required],
      isVoiceMail: [this.data.notification ? this.data.notification.isVoicemail : false],
      fields: this.formBuilder.array([]),
      targets: this.formBuilder.array([])
    })

    if (this.data.notification) {
      this.initNotificationFieldArray();
    }
    this.initNotificationTargetArray()
  }

  ngOnChanges(){
    console.log("changes applied")
  }

  onSubmit() {
    if (this.notificationForm.valid) {
      this.isLoading = true;
      const formValues = this.notificationForm.value;
      if (this.data.notification) {
        this.updateNotification(formValues);
      }
      else {
        this.createNotification(formValues);
      }
    }
  }

  initNotificationFieldArray() {
    this.data.notification.formEntries.forEach(entry => {
      this.fields.push(this.formBuilder.group({
        id: [entry.id, Validators.required],
        fieldName: [entry.fieldName, FIELD_NAME_VALIDATOR],
        fieldDescription: [entry.fieldDescription, FIELD_DESCRIPTION_VALIDATOR],
        fieldKey: [entry.fieldKey, FIELD_KEY_VALIDATOR]
      }));
    });
  }

  initNotificationTargetArray() {
    const targets = this.data.notification.target.split(TARGET_SPLIT_CHAR);
    alert(targets)
    targets.forEach(target => {
      this.targets.push(this.formBuilder.group({
        target: [target]
      }));
    });
  }

  onAddField() {
    this.fields.push(this.formBuilder.group({
      id: [''],
      fieldName: ['', FIELD_NAME_VALIDATOR],
      fieldDescription:['',FIELD_DESCRIPTION_VALIDATOR],
      fieldKey: ['', FIELD_KEY_VALIDATOR]
    }));
  }
  onAddTargets() {
    this.targets.push(this.formBuilder.group({
      target: [""]
    }))
  }


  onRemoveField(index: number) {
    this.fields.removeAt(index);
  }

  onRemoveTarget(index: number) {
    this.targets.removeAt(index);
  }

  get fields(): FormArray<FormGroup> {
    return this.notificationForm.controls['fields'] as FormArray<FormGroup>;
  }
  get targets(): FormArray<FormGroup> {
    return this.notificationForm.controls['targets'] as FormArray<FormGroup>;
  }

  getFieldsValue(): FormEntryResponse[] {
    const values: FormEntryResponse[] = [];

    for (let index = 0; index < this.fields.length; index++) {
      const formGroupValue = this.fields.controls[index].value;
      const fieldId: number = parseInt(formGroupValue['id']);

      if (this.data.notification) {
        values.push({
          id: isNaN(fieldId) ? undefined : fieldId,
          fieldName: formGroupValue['fieldName'],
          fieldDescription: formGroupValue['fieldDescription'],
          fieldType:formGroupValue['fieldType'],
          fieldKey: formGroupValue['fieldKey'],
          notificationId: this.data.notification.id
        });
      } else {
        values.push({
          id: isNaN(fieldId) ? undefined : fieldId,
          fieldName: formGroupValue['fieldName'],
          fieldDescription: formGroupValue['fieldDescription'],
          fieldType:formGroupValue['fieldType'],
          fieldKey: formGroupValue['fieldKey']
        });
      }
    }

    return values;
  }

  getTargetValue(): string {
    return this.targets.
    controls
    .map(formControl=>formControl.value["target"])
    .join(',');
  }

  private createNotification(formValues: any) {
    const request: CreateNotificationIntegrationRequest = {
      aiAgentId: this.data.aiAgent.id,
      target: this.getTargetValue(),
      type: formValues["type"],
      isVoicemail: formValues["isVoiceMail"],
      formEntries: this.getFieldsValue()
    }

    console.log(request)


    this.notificationIntegrationService.create(request).subscribe({
      next: (notification) => {
        this.isLoading = false;
        this.aiConfigurationPageEventService.onNotificationIntegrationAddedEvent.emit(notification);
        this.dialogRef.close();
        this.snackBar.open('Notification created successfully', 'Close', { duration: 5000 });
      },
      error: () => {
        alert('Server Error, contact support');
        this.isLoading = false;
      }
    })
  }

  private updateNotification(formValues: any) {
    const request: UpdateNotificationIntegrationRequest = {
      notificationId: this.data.notification.id,
      target: this.getTargetValue(),
      type: formValues["type"],
      isVoicemail: formValues["isVoiceMail"],
    }
    const formEntries = this.getFieldsValue();
    this.notificationIntegrationService.update(request).subscribe({
      next: (notification) => {
        this.deleteFormEntries(formEntries);
        this.updateFormEntries(formEntries)
        this.aiConfigurationPageEventService.onRefreshAiAgentEvent.emit();
      },
      error: () => {
        alert('Server Error, contact support');
        this.isLoading = false;
      }
    })
  }

  private deleteFormEntries(formEntries: FormEntryResponse[]) {
    const formEntriesToDelete: (number)[] = this.data.notification.formEntries
      .filter(formEntry => !formEntries.some(fe => fe.id == formEntry.id))
      .map(fe => fe.id!);

    if (formEntriesToDelete.length > 0) {
      this.formEntryService.deleteEntries(formEntriesToDelete).subscribe({
        next: () => {
        },
        error: () => {
          alert('Server error, contact support');
        }
      });
    }
  }

  private updateFormEntries(formEntries: FormEntryResponse[]) {
    this.formEntryService.upsertBatchEntries(formEntries, this.data.aiAgent.id).subscribe({
      next: () => {
        this.isLoading = false;
        this.dialogRef.close();
        this.aiConfigurationPageEventService.onRefreshAiAgentEvent.emit();
      },
      error: () => {
        alert('Server Error, contact support');
        this.isLoading = false;
      }
    });
  }

}
